import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { OpenAccessPass } from './open-access-pass/open-access-pass.js';

export class OpenAccessPassUpdatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = 'fd0f428b3cf2417595f5a0366baa59be';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly before: OpenAccessPass,
      public readonly after: OpenAccessPass,
  ) {
    super();
  }

  static create(args: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: dayjs.Dayjs,
    source: SyncEventSource,
    before: OpenAccessPass,
    after: OpenAccessPass,
  }): OpenAccessPassUpdatedEvent {
    return new OpenAccessPassUpdatedEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.before,
        args.after,
    );
  }

  get $typeId(): string {
    return OpenAccessPassUpdatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassUpdatedEvent {
    return new OpenAccessPassUpdatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        context.fromData<OpenAccessPass>(MapUtils.get<SerializedData>(map, 'before')),
        context.fromData<OpenAccessPass>(MapUtils.get<SerializedData>(map, 'after')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'beforeId': this.before.uniqueId,
      'before': context.toData(this.before),
      'afterId': this.after.uniqueId,
      'after': context.toData(this.after),
    };
  }

  clone(): this {
    return new OpenAccessPassUpdatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.before.clone(),
        this.after.clone(),
    ) as this;
  }

  isSame(other: OpenAccessPassUpdatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.before, this.before) &&
        isSameData(other.after, this.after);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenAccessPassUpdatedEvent;
  }

  static assertType(dm: DataModel): dm is OpenAccessPassUpdatedEvent {
    return dm instanceof OpenAccessPassUpdatedEvent;
  }
}

export class OpenAccessPassUpdatedEventBuilder extends DataModelBuilder<OpenAccessPassUpdatedEvent> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassUpdatedEvent {
    return OpenAccessPassUpdatedEvent.fromMap(context, map);
  }
}
